import React from 'react';
import { observer } from 'mobx-react';
import { Body, Theme } from '@routific/hive';
import styled from 'styled-components';
import { OrderCompletionStatus, VisitDtoModelType } from '../../../store/mst/models';
import { TimelineViolationTypes } from '../../../helpers/lateness';
import VisitIcon from './VisitIcon';
import VisitName from './VisitName';
import OrderTimeWindow from './OrderTimeWindow';
import { StyledSkeleton } from './styles';
import { IColor } from '../../../types/IColor';
import { ORDER_WARNING_COLOR } from './constants';
import LocationExceptionWarning from '../LocationExceptionWarning';

interface Props {
  visit: VisitDtoModelType;
  loading?: boolean;
  stopUuid?: string;
  color?: IColor;
  orderNumber?: string | null;
}

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Theme.Spacing.smSpacing};
  flex: 1;
  min-width: 0;
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Theme.Spacing.xsSpacing};
  flex: 1;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Theme.Spacing.smSpacing};
  min-width: 0;
`;

const NameWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrderNumberBody = styled(Body)`
  flex-shrink: 0;
  text-align: right;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OrderStatus: React.FC<Props> = ({
  visit, loading, stopUuid, color, orderNumber,
}) => {
  const attemptForStop = visit?.attempt?.(stopUuid);
  const isCompleted =
    attemptForStop?.status === OrderCompletionStatus.MISSED ||
    attemptForStop?.status === OrderCompletionStatus.DELIVERED;
  const hasLocationException = !!attemptForStop?.hasLocationException;
  const hasLatenessViolation = !!visit?.timeline?.violations?.find(
    (v: { type: string }) =>
      v.type === TimelineViolationTypes.LATENESS || v.type === TimelineViolationTypes.EARLINESS,
  );
  const isWarning = (hasLatenessViolation && !isCompleted) || !!visit?.isDateMismatched;

  const isLoading = !!loading;
  let orderNumberContent: React.ReactNode = null;

  if (isLoading) {
    orderNumberContent = <StyledSkeleton active paragraph={false} title={{ width: 60 }} />;
  } else if (orderNumber) {
    orderNumberContent = (
      <OrderNumberBody
        type={isWarning ? undefined : 'secondary'}
        style={isWarning ? { color: ORDER_WARNING_COLOR } : undefined}
      >
        {orderNumber}
      </OrderNumberBody>
    );
  }

  return (
    <Row>
      <VisitIcon visit={visit} stopUuid={stopUuid} color={color} />
      <ContentColumn>
        <TopRow>
          <NameWrapper>
            <VisitName displayName={visit.displayName} isCompleted={isCompleted} loading={isLoading} />
          </NameWrapper>
          {orderNumberContent}
        </TopRow>
        <OrderTimeWindow
          startTimeString={visit.activeTimeWindow?.startTimeString}
          endTimeString={visit.activeTimeWindow?.endTimeString}
          rangeString={visit.timeWindowString}
        />
        {hasLocationException && <LocationExceptionWarning variant="label" />}
      </ContentColumn>
    </Row>
  );
};

export default observer(OrderStatus);
